'use client';

import { FC, useState } from 'react';

import { ProductUtils, ShoppingCartUtils } from '@ravago/shared/next-data-access/client';
import { SystemPageType } from '@ravago/shared/page-data/enums/system-page-type';
import { ShoppingCartItem, ShoppingCartItemWithOptionalSlug } from '@ravago/shared/page-data/models/features';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Breadcrumbs } from '@ravago/shared/radiance/components/breadcrumbs/components/Breadcrumbs/Breadcrumbs';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { Grid } from '@ravago/shared/radiance/components/grid/components/Grid/Grid';
import { GridCol } from '@ravago/shared/radiance/components/grid/components/GridCol/GridCol';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { Spinner } from '@ravago/shared/radiance/components/spinner/components/Spinner/Spinner';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { useShoppingCartContext } from '../../../../context/shopping-cart-context';
import OrderNotFoundIllustration from '../../../illustrations/order-not-found-illustration';
import { EmptyState } from '../../../shared/empty-state/empty-state.component';
import { Link } from '../../../shared/link/link';
import { SystemPageLink } from '../../system-page-link/system-page-link';

import { ShoppingCartItemElement } from './shopping-cart-item-element';
import { ShoppingCartOrderSummary } from './shopping-cart-order-summary';

interface Props {
   initialShoppingCartItems: Array<ShoppingCartItem>;
   languageCode: string;
   myPricesRoute: string | null;
   locale: string;
}

export const ShoppingCartElement: FC<Props> = ({ initialShoppingCartItems, languageCode, myPricesRoute, locale }) => {
   const translate = useTranslations();
   const [shoppingCartItems, setShoppingCartItems] = useState<Array<ShoppingCartItem>>(initialShoppingCartItems);
   const [refetchingShoppingCart, setRefetchingShoppingCart] = useState<boolean>(false);
   const { removeFromCart } = useShoppingCartContext();

   const handleRemoveFromShoppingCart = async (shoppingCartItemId: ShoppingCartItem['id']) => {
      setRefetchingShoppingCart(true);
      const removalStatus = await ShoppingCartUtils.removeFromShoppingCart(shoppingCartItemId);
      removeFromCart(removalStatus);
      const cart = await ShoppingCartUtils.getShoppingCart();
      const shoppingCartItemsWithSlug = await Promise.all(
         (cart.items ?? []).map(async (item) => {
            if (item.product.baseProductId) {
               const productId = item.product.baseProductId;
               try {
                  const contentfulProduct = await ProductUtils.getProductByEntryId(languageCode, `product-${productId}`);
                  const slug = contentfulProduct?.slug;
                  const itemWithOptionalSlug: ShoppingCartItemWithOptionalSlug = {
                     ...item,
                     slug
                  };
                  return itemWithOptionalSlug;
               } catch (_) {
                  return item;
               }
            }

            return item;
         })
      );
      setShoppingCartItems(shoppingCartItemsWithSlug);
      setRefetchingShoppingCart(false);
   };

   if (shoppingCartItems.length === 0) {
      return (
         <Grid>
            <GridCol spanDesktop={6} spanTablet={6} spanPhone={4} startDesktop={4} startTablet={2} startPhone={1}>
               <Box direction="column" items="center">
                  <EmptyState
                     image={<OrderNotFoundIllustration />}
                     title={translate('shopping-cart.empty-title')}
                     content={translate('shopping-cart.empty-description')}
                     button={
                        <Box items="center">
                           <SystemPageLink baseRoute={myPricesRoute} type={SystemPageType.MY_PRODUCTS_AND_PRICES} locale={locale}>
                              <Button>{translate('shopping-cart.my-prices')}</Button>
                           </SystemPageLink>
                        </Box>
                     }
                  />
               </Box>
            </GridCol>
         </Grid>
      );
   }

   if (refetchingShoppingCart) {
      return (
         <Box direction="column" items="center">
            <Spinner />
         </Box>
      );
   }

   const cartItems = shoppingCartItems.map((shoppingCartItem: ShoppingCartItem) => (
      <ShoppingCartItemElement
         locale={locale}
         key={shoppingCartItem.id}
         data-testid="shopping-cart__shopping-cart-item"
         removeFromShoppingCart={handleRemoveFromShoppingCart}
         shoppingCartItem={shoppingCartItem}
      />
   ));

   return (
      <Box direction="column">
         <Breadcrumbs>
            <Link href={`/${languageCode}`} prefetch={false}>
               <Icon icon="home" />
            </Link>
            {translate('shopping-cart.header-shopping-cart')}
         </Breadcrumbs>

         <Typography component="h1" variant="heading-2">
            {translate('shopping-cart.header-shopping-cart')}
         </Typography>

         <Typography variant="caption">
            {shoppingCartItems.length}{' '}
            {shoppingCartItems.length > 1 ? translate('shopping-cart.items-count.plural') : translate('shopping-cart.items-count.single')}
         </Typography>

         <Grid>
            <GridCol spanDesktop={8} spanTablet={8} spanPhone={4}>
               <Box direction="column" gap="sm">
                  {cartItems}
               </Box>
            </GridCol>
            <BreakpointSwitch excludeViews={['phone', 'tab-port']}>
               <GridCol spanDesktop={4}>
                  <ShoppingCartOrderSummary languageCode={languageCode} shoppingCartItems={shoppingCartItems} />
               </GridCol>
            </BreakpointSwitch>
         </Grid>

         <BreakpointSwitch includeViews={['phone', 'tab-port']}>
            <Box position="fixed" zIndex="39" width="90%" bottom="0" direction="column" gap="2xs">
               <ShoppingCartOrderSummary languageCode={languageCode} shoppingCartItems={shoppingCartItems} />
            </Box>
         </BreakpointSwitch>
      </Box>
   );
};
